/**
 * customContentMocks — fallback fixtures for CustomContentService.
 *
 * Sprint 4 (#54): extracted from CustomContentService.js to keep
 * CustomContentCRUD.js under the 800-LOC limit (CLAUDE.md Rule 3). These
 * fixtures are returned by `getMockContentList(...)` when the GraphQL
 * backend is unavailable during development.
 *
 * NOTE — Internal module: import from CustomContentService instead.
 */

export const MOCK_CONTENT_FIXTURES = [
  {
    contentId: '1',
    title: 'Q4 Market Outlook',
    category: 'Market Analysis',
    section: 'Market Overview', // NEW: US-040
    content: 'As we approach the end of the year, market conditions continue to show resilience despite global uncertainties. Our analysis suggests maintaining a balanced approach with slight overweight to quality growth stocks.',
    tags: ['quarterly', 'outlook', 'analysis'],
    createdAt: '2024-01-15T10:30:00Z',
    updatedAt: '2024-01-20T14:45:00Z',
    lastUsedAt: '2024-01-22T09:15:00Z',
    usageCount: 15,
    usedInTemplates: ['template_1', 'template_3'],
    version: 2,
    wordCount: 45,
    metadata: {
      wordCount: 45,
      characterCount: 267,
      estimatedReadTime: '1 min read'
    }
  },
  {
    contentId: '2',
    title: 'Client Education: Bond Basics',
    category: 'Education',
    section: 'Education Corner', // NEW: US-040
    content: 'Understanding bonds is fundamental to building a diversified portfolio. Bonds provide steady income and can help reduce overall portfolio volatility during market downturns.',
    tags: ['education', 'bonds', 'portfolio'],
    createdAt: '2024-01-10T09:15:00Z',
    updatedAt: '2024-01-18T16:20:00Z',
    lastUsedAt: '2024-01-19T11:30:00Z',
    usageCount: 8,
    usedInTemplates: ['template_2'],
    version: 1,
    wordCount: 32,
    metadata: {
      wordCount: 32,
      characterCount: 198,
      estimatedReadTime: '1 min read'
    }
  },
  {
    contentId: '3',
    title: 'Compliance Notice: Annual Reviews',
    category: 'Compliance & Tax',
    section: 'Action Items', // NEW: US-040
    content: 'Annual portfolio reviews are an important part of our service commitment. Please schedule your review to ensure your investment strategy aligns with your current goals.',
    tags: ['compliance', 'reviews', 'annual'],
    createdAt: '2024-01-12T14:00:00Z',
    updatedAt: '2024-01-17T10:30:00Z',
    lastUsedAt: '2024-01-21T15:45:00Z',
    usageCount: 12,
    usedInTemplates: ['template_1', 'template_4'],
    version: 3,
    wordCount: 38,
    metadata: {
      wordCount: 38,
      characterCount: 234,
      estimatedReadTime: '1 min read'
    }
  },
  {
    contentId: '4',
    title: 'Tax Planning Strategies',
    category: 'Compliance & Tax',
    section: 'Action Items', // NEW: US-040
    content: 'Year-end tax planning can help optimize your financial position. Consider tax-loss harvesting, maximizing retirement contributions, and reviewing charitable giving strategies.',
    tags: ['tax', 'planning', 'year-end'],
    createdAt: '2024-01-08T11:45:00Z',
    updatedAt: '2024-01-16T13:15:00Z',
    lastUsedAt: '2024-01-20T09:00:00Z',
    usageCount: 6,
    usedInTemplates: ['template_3'],
    version: 1,
    wordCount: 29,
    metadata: {
      wordCount: 29,
      characterCount: 178,
      estimatedReadTime: '1 min read'
    }
  },
  {
    contentId: '5',
    title: 'Investment Philosophy',
    category: 'Firm Information',
    section: 'Advisor Commentary', // NEW: US-040
    content: 'Our investment approach is based on long-term value creation through disciplined asset allocation, risk management, and regular portfolio rebalancing.',
    tags: ['philosophy', 'long-term', 'allocation'],
    createdAt: '2024-01-05T16:30:00Z',
    updatedAt: '2024-01-14T12:00:00Z',
    lastUsedAt: '2024-01-18T14:20:00Z',
    usageCount: 10,
    usedInTemplates: ['template_2', 'template_5'],
    version: 2,
    wordCount: 25,
    metadata: {
      wordCount: 25,
      characterCount: 156,
      estimatedReadTime: '1 min read'
    }
  },
  {
    contentId: '6',
    title: 'Client Contact Reminder',
    category: 'Client Communication',
    section: 'Advisor Commentary', // NEW: US-040
    content: 'We wanted to reach out to schedule your quarterly review. Please contact us at your earliest convenience to discuss your portfolio performance and any changes to your financial situation.',
    tags: ['communication', 'review', 'quarterly'],
    createdAt: '2024-01-22T08:00:00Z',
    updatedAt: '2024-01-22T08:00:00Z',
    lastUsedAt: null,
    usageCount: 0,
    usedInTemplates: [],
    version: 1,
    wordCount: 31,
    metadata: {
      wordCount: 31,
      characterCount: 189,
      estimatedReadTime: '1 min read'
    }
  }
];

/**
 * Filter, sort, and paginate the mock fixtures the same way the GraphQL
 * pipeline would. Used by `CustomContentService.getMockContentList(...)` as
 * a development fallback.
 */
export function getMockContentList(options = {}) {
  const {
    page = 1,
    limit = 20,
    search = '',
    category = '',
    sortBy = 'createdAt',
    sortOrder = 'desc'
  } = options;

  // Clone fixtures so filters/sorts don't mutate the shared array.
  let mockContent = MOCK_CONTENT_FIXTURES.map(item => ({ ...item }));

  if (search) {
    mockContent = mockContent.filter(item =>
      item.title.toLowerCase().includes(search.toLowerCase()) ||
      item.content.toLowerCase().includes(search.toLowerCase()) ||
      item.tags.some(tag => tag.toLowerCase().includes(search.toLowerCase()))
    );
  }

  if (category && category !== 'all') {
    mockContent = mockContent.filter(item => item.category === category);
  }

  mockContent.sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case 'title':
        aValue = a.title.toLowerCase();
        bValue = b.title.toLowerCase();
        break;
      case 'usageCount':
        aValue = a.usageCount;
        bValue = b.usageCount;
        break;
      case 'lastUsedAt':
        aValue = new Date(a.lastUsedAt || 0);
        bValue = new Date(b.lastUsedAt || 0);
        break;
      default: // createdAt
        aValue = new Date(a.createdAt);
        bValue = new Date(b.createdAt);
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const total = mockContent.length;
  const totalPages = Math.ceil(total / limit);
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedContent = mockContent.slice(startIndex, endIndex);

  return {
    content: paginatedContent,
    pagination: {
      page,
      limit,
      total,
      totalPages
    },
    isMock: true // Mock/fallback data
  };
}
